import { type GetServerSideProps } from 'next';

import { cmsService, localizedContentService } from '../../services';

import { withEnhancedPageHandler } from '../../middlewares/withEnhancedPageHandler';

import { applyCorrelationIdHeader } from '../../utils/server/applyCorrelationIdHeader';

import GeolocTemplate from '../../templates/Geoloc/GeolocTemplate';

import { breadcrumbTagger } from '../../data/geoloc/structuredData';
import { modifiers } from '../../data/shared';

import {
  type IGeolocPageProps,
  type IGeolocLocalizedContent,
} from '../../modules/geoloc/pageProps/@interfaces';
import { seoDataService } from '../../services/seoDataService';
import { type IServiceByCity } from '../../modules/shared/@interfaces';

export const Geoloc = (props: IGeolocPageProps) => (
  <GeolocTemplate {...props} />
);

const ROOT_SLUGS = [
  {
    text: 'Assistência técnica',
    slug: 'assistencia-tecnica',
  },
  {
    text: 'Aulas',
    slug: 'aulas',
  },
  {
    text: 'Autos',
    slug: 'automoveis',
  },
  {
    text: 'Consultoria',
    slug: 'consultoria',
  },
  {
    text: 'Design e Tecnologia',
    slug: 'design-e-tecnologia',
  },
  {
    text: 'Eventos',
    slug: 'eventos',
  },
  {
    text: 'Moda e beleza',
    slug: 'moda-e-beleza',
  },
  {
    text: 'Reformas e reparos',
    slug: 'reformas-e-reparos',
  },
  {
    text: 'Saúde',
    slug: 'saude',
  },
  {
    text: 'Serviços Domésticos',
    slug: 'familia',
  },
];

const getServerSidePropsHandler: GetServerSideProps = async ({
  params,
  res,
  req,
}) => {
  const geolocSlug = (params.place as string[]).join('/');

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  );

  const customHeaders = applyCorrelationIdHeader(req.headers);

  const [serviceByCity] = await cmsService<IServiceByCity>(
    'api/services-by-cities',
    {
      'filters[slug][$eq]': geolocSlug,
      populate: '*',
    },
    customHeaders
  );

  if (!serviceByCity) {
    throw new Error('City not Found');
  }

  const cityContent = serviceByCity.attributes;

  const servicesByCities = await cmsService<IServiceByCity>(
    'api/services-by-cities',
    {
      'pagination[limit]': '-1',
    },
    customHeaders
  );

  const allCities = servicesByCities.map((s) => s.attributes);

  const breadcrumbs = [
    {
      name: 'GetNinjas',
      url: '/',
    },
    {
      name: cityContent.city,
      url: `/local/${geolocSlug}`,
    },
  ];

  const structuredData = breadcrumbTagger(cityContent.city, geolocSlug);

  const pageInfos = await seoDataService<string[]>({
    slugs: ROOT_SLUGS.map((s) => `${s.slug}/${geolocSlug}`),
    customHeaders,
  });

  const categoryTree = ROOT_SLUGS.map((c, i) => ({
    name: c.text,
    items: pageInfos[i] ? JSON.parse(pageInfos[i]).children_flat : [],
  })).filter((c) => c.items.length > 0);

  const { footerContent, headerContent } =
    localizedContentService<IGeolocLocalizedContent>(
      'pt-BR',
      ['footerContent', 'headerContent'],
      [modifiers.STATIC_ASSETS_PATH, modifiers.CURRENT_YEAR]
    );

  return {
    props: {
      ...cityContent,
      breadcrumbs,
      allCities,
      categoryTree,
      structuredData,
      footerContent,
      headerContent,
    },
  };
};

export const getServerSideProps = withEnhancedPageHandler(
  getServerSidePropsHandler
);

export default Geoloc;
